/**
 * AuditLogsTab
 *
 * Admin → Audit Logs / Error Logs tab. Cursor pagination so the page stays
 * fast at millions of rows. Filter changes reset the cursor.
 *
 * One component serves three views (`defaultMode` prop):
 *   - 'audit'   — security/compliance events from auditlogs collection
 *   - 'errors'  — application errors from errorlogs collection
 *                 (with an additional "Group by signature" toggle that
 *                 swaps the underlying read endpoint for the fingerprint
 *                 aggregation)
 *   - 'both'    — merged client-side, sorted by createdAt desc
 *
 * The mode is also user-switchable via a segmented control at the top, so
 * both the in-page toggle (one URL) and the dedicated sidebar entry
 * (separate URL with `defaultMode='errors'`) point at the same component.
 *
 * Phase 2 (planned) — see docs/audit-log-roadmap.md:
 *   - Sentry / DataDog mirror for errors
 *   - External S3 Object Lock mirror for audit
 *   - Daily summary email + anomaly detection
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  FiSearch, FiShield, FiDownload, FiCheckCircle,
  FiAlertCircle, FiUser, FiAlertTriangle, FiActivity, FiX,
} from 'react-icons/fi';
import { auditLogsAPI, errorLogsAPI } from '../../services/api';
import AuditLogDetailModal from './AuditLogDetailModal';
import InfoPopover from '../common/InfoPopover';
import RefreshButton from '../common/RefreshButton';
import ViewButton from '../common/ViewButton';
import Button from '../common/Button';

// ---------------------------------------------------------------------------
// Audit mode constants
// ---------------------------------------------------------------------------
const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'auth', label: 'Authentication' },
  { value: 'admin.user', label: 'Admin · Users' },
  { value: 'admin.role', label: 'Admin · Roles & Permissions' },
  { value: 'admin.ai_model', label: 'Admin · AI Models' },
  { value: 'admin.service', label: 'Admin · Services' },
  { value: 'company', label: 'Company edits' },
  { value: 'assignment', label: 'Professional assignments' },
  { value: 'draft', label: 'Document drafts' },
  { value: 'file', label: 'Files / downloads' },
  { value: 'system', label: 'System' },
];

const CATEGORY_PALETTE = {
  auth: { bg: '#e3f0ed', color: '#145f5f' },
  'admin.user': { bg: '#f3ebd7', color: '#836016' },
  'admin.role': { bg: '#edeef3', color: '#3a4a66' },
  'admin.ai_model': { bg: '#f3ebd7', color: '#836016' },
  'admin.service': { bg: '#edeef3', color: '#3a4a66' },
  company: { bg: '#e8efe5', color: '#4a6b3f' },
  assignment: { bg: '#f3ebd7', color: '#8a6a1f' },
  draft: { bg: '#efe9e0', color: '#6f6049' },
  file: { bg: '#edeef3', color: '#3a4a66' },
  system: { bg: '#f0ebe0', color: '#7a7060' },
};

// ---------------------------------------------------------------------------
// Error mode constants
// ---------------------------------------------------------------------------
const ERROR_LEVEL_OPTIONS = [
  { value: '', label: 'All levels' },
  { value: 'critical', label: 'Critical (5xx / uncaught)' },
  { value: 'error',    label: 'Error (4xx)' },
  { value: 'warn',     label: 'Warn (422 validation)' },
];

const ERROR_SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'backend', label: 'Backend' },
  { value: 'frontend', label: 'Frontend' },
];

const ERROR_LEVEL_PALETTE = {
  critical: { bg: '#f2e9e6', color: '#9a564a' },
  error:    { bg: '#f2e9e6', color: '#9a564a' },
  warn:     { bg: '#f3ebd7', color: '#8a6a1f' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch (e) {
    return String(d);
  }
}

function summarizeAudit(row) {
  const det = row.details || {};
  if (row.action === 'company.update' && det.diff) {
    const keys = Object.keys(det.diff || {}).filter((k) => k !== '_t');
    return `${keys.length} field${keys.length === 1 ? '' : 's'} changed on ${row.targetName || 'company'}`;
  }
  if (row.action === 'assignment.access_level_changed') return `Access ${det.from || '?'} → ${det.to || '?'}`;
  if (row.action === 'admin.user.role_change') return `${det.previousRole || '?'} → ${det.newRole || '?'}`;
  if (row.action === 'user.login' || row.action === 'user.login_password') return det.method ? `method=${det.method}` : 'logged in';
  if (row.action === 'user.login_failed' || row.action === 'user.login_password_failed') return det.reason ? `reason: ${det.reason}` : 'failed login';
  if (row.action === 'file.downloaded') return `${det.format || ''} · ${det.surface || ''}`;
  if (row.action === 'admin.user.marked_compromised') return 'Marked as compromised';
  return '';
}

function shortFingerprint(fp) {
  if (!fp) return '';
  return fp.slice(0, 8);
}

// Labeled filter wrapper used by every control in the filters grid below.
// Always renders a label above the field, and in "Both" mode prefixes a
// subtle scope hint (Audit · / Errors ·) so admins can tell which side of
// the merged view a filter applies to.
function FilterField({ label, scopeLabel, scopeTone, className = '', children }) {
  const scopeColor = scopeTone === 'errors' ? '#c0432c' : '#145f5f';
  return (
    <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
      <label className="flex items-center gap-1 text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[#7a7060]">
        {scopeLabel && (
          <>
            <span className="inline-block w-[3px] h-[8px] rounded-full" style={{ background: scopeColor, opacity: 0.7 }} />
            <span style={{ color: scopeColor }}>{scopeLabel}</span>
            <span className="text-[#b8b0a0]">·</span>
          </>
        )}
        <span>{label}</span>
      </label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function AuditLogsTab({ defaultMode = 'audit', headerActionsEl = null } = {}) {
  const location = useLocation();
  // Prefill targetId/actorId/requestId/q from URL query params so deep links
  // from the entity audit history panel land on a pre-filtered view.
  const initialFilters = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    return {
      actionCategory: '',
      success: '',
      errorSource: '',
      errorLevel: '',
      fingerprint: sp.get('fingerprint') || '',
      q: sp.get('q') || '',
      actorId: sp.get('actorId') || '',
      targetId: sp.get('targetId') || '',
      requestId: sp.get('requestId') || '',
      dateFrom: '',
      dateTo: '',
    };
    // location.search read once on mount; rerunning on later changes is handled below
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL `?mode=audit|errors|both` wins over the prop-level defaultMode, so a
  // deep-link from "View full history" (which scopes by targetId) lands on the
  // audit-only stream where targetId actually filters something.
  const initialMode = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const m = sp.get('mode');
    if (m === 'audit' || m === 'errors' || m === 'both') return m;
    return defaultMode;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [mode, setMode] = useState(initialMode); // 'audit' | 'errors' | 'both'
  // When true, the active tab uses a server-side aggregation:
  //   audit  → groups by action (e.g. "user.login: 312 times")
  //   errors → groups by stack fingerprint
  // Hidden in 'both' mode (the two aggregations don't merge cleanly).
  const [groupBy, setGroupBy] = useState(false);

  // Filters are mostly shared but adapt per mode.
  const [filters, setFilters] = useState(initialFilters);

  // If the URL changes while the tab is mounted (e.g. user clicks another deep
  // link), reflect the new mode/targetId/actorId/requestId in the state.
  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    const next = {
      q: sp.get('q') || '',
      actorId: sp.get('actorId') || '',
      targetId: sp.get('targetId') || '',
      requestId: sp.get('requestId') || '',
      fingerprint: sp.get('fingerprint') || '',
    };
    setFilters((prev) => {
      if (
        prev.q === next.q &&
        prev.actorId === next.actorId &&
        prev.targetId === next.targetId &&
        prev.requestId === next.requestId &&
        prev.fingerprint === next.fingerprint
      ) {
        return prev;
      }
      return { ...prev, ...next };
    });
    const m = sp.get('mode');
    if (m === 'audit' || m === 'errors' || m === 'both') {
      setMode(m);
    }
  }, [location.search]);

  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]); // grouped-by-fingerprint rows (errors only)
  // Single-mode cursor stack (audit OR errors). Each entry is a string cursor.
  const [cursorStack, setCursorStack] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  // Both-mode cursor stack. Each entry is a `{ audit, errors }` pair so we
  // can advance the two underlying streams independently and reconstruct the
  // merged page at any depth.
  const [bothCursorStack, setBothCursorStack] = useState([{ audit: null, errors: null }]);
  const [bothNextCursors, setBothNextCursors] = useState({ audit: null, errors: null });
  const [bothHasMore, setBothHasMore] = useState({ audit: false, errors: false });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Verify integrity is audit-only.
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Re-sync mode when the parent's `defaultMode` changes (e.g. tab switch
  // inside AdminDashboard). The URL-mode override above takes precedence; if
  // it's present we don't fall back to the prop.
  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    const m = sp.get('mode');
    if (m === 'audit' || m === 'errors' || m === 'both') return;
    setMode(defaultMode);
  }, [defaultMode, location.search]);

  // ----- Data fetching ---------------------------------------------------
  const fetchPage = useCallback(
    async (cursor) => {
      setLoading(true);
      try {
        if (mode === 'errors' && groupBy) {
          // Server-side aggregation; no cursor (limit applied server-side).
          const params = {
            q: filters.q,
            source: filters.errorSource,
            level: filters.errorLevel,
            fingerprint: filters.fingerprint,
            requestId: filters.requestId,
            actorId: filters.actorId,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            limit: 50,
          };
          const res = await errorLogsAPI.getGrouped(params);
          setGroups(res?.data?.groups || []);
          setRows([]);
          setNextCursor(null);
          setHasMore(false);
        } else if (mode === 'errors') {
          const params = {
            q: filters.q,
            source: filters.errorSource,
            level: filters.errorLevel,
            fingerprint: filters.fingerprint,
            requestId: filters.requestId,
            actorId: filters.actorId,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            limit: 50,
          };
          if (cursor) params.cursor = cursor;
          const res = await errorLogsAPI.getAll(params);
          const data = res?.data || {};
          setRows(data.rows || []);
          setGroups([]);
          setNextCursor(data.nextCursor || null);
          setHasMore(!!data.hasMore);
        } else if (mode === 'audit' && groupBy) {
          // Server-side aggregation by `action`; default window: last 30 days.
          const params = {
            actionCategory: filters.actionCategory,
            success: filters.success,
            q: filters.q,
            actorId: filters.actorId,
            targetId: filters.targetId,
            requestId: filters.requestId,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            limit: 50,
          };
          const res = await auditLogsAPI.getGrouped(params);
          setGroups(res?.data?.groups || []);
          setRows([]);
          setNextCursor(null);
          setHasMore(false);
        } else if (mode === 'audit') {
          const params = {
            actionCategory: filters.actionCategory,
            success: filters.success,
            q: filters.q,
            actorId: filters.actorId,
            targetId: filters.targetId,
            requestId: filters.requestId,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            limit: 50,
          };
          if (cursor) params.cursor = cursor;
          const res = await auditLogsAPI.getAll(params);
          const data = res?.data || {};
          setRows(data.rows || []);
          setGroups([]);
          setNextCursor(data.nextCursor || null);
          setHasMore(!!data.hasMore);
        } else {
          // 'both' — fetch in parallel from both streams, merge by createdAt
          // desc client-side. Each stream paginates independently so we keep
          // separate cursor state per side; the merged view shows the most
          // recent 50 across whichever pair of pages the user has navigated to.
          const pair = (cursor && typeof cursor === 'object') ? cursor : { audit: null, errors: null };
          const sharedParams = {
            q: filters.q,
            actorId: filters.actorId,
            requestId: filters.requestId,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            limit: 50,
          };
          const auditParams = { ...sharedParams, actionCategory: filters.actionCategory, success: filters.success, targetId: filters.targetId };
          const errorParams = { ...sharedParams, source: filters.errorSource, level: filters.errorLevel, fingerprint: filters.fingerprint };
          if (pair.audit) auditParams.cursor = pair.audit;
          if (pair.errors) errorParams.cursor = pair.errors;
          const [auditRes, errorRes] = await Promise.all([
            auditLogsAPI.getAll(auditParams),
            errorLogsAPI.getAll(errorParams),
          ]);
          const auditRows = (auditRes?.data?.rows || []).map((r) => ({ ...r, __kind: 'audit' }));
          const errorRows = (errorRes?.data?.rows || []).map((r) => ({ ...r, __kind: 'error' }));
          const merged = [...auditRows, ...errorRows]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 50);
          setRows(merged);
          setGroups([]);
          setBothNextCursors({
            audit: auditRes?.data?.nextCursor || null,
            errors: errorRes?.data?.nextCursor || null,
          });
          setBothHasMore({
            audit: !!auditRes?.data?.hasMore,
            errors: !!errorRes?.data?.hasMore,
          });
        }
      } catch (err) {
        console.error('Failed to fetch logs', err);
        setRows([]);
        setGroups([]);
        setNextCursor(null);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [filters, mode, groupBy]
  );

  // Refetch when filters or mode change — reset both cursor stacks.
  useEffect(() => {
    setCursorStack([null]);
    setBothCursorStack([{ audit: null, errors: null }]);
    if (mode === 'both') {
      fetchPage({ audit: null, errors: null });
    } else {
      fetchPage(null);
    }
  // We intentionally depend on fetchPage which already depends on filters/mode/groupBy
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const nextPage = () => {
    if (mode === 'both') {
      // Advance whichever side still has more. If both are exhausted, do nothing.
      if (!bothHasMore.audit && !bothHasMore.errors) return;
      const pair = {
        // If a side has no more pages, freeze its cursor at null so subsequent
        // back-and-forth doesn't accidentally rewind.
        audit: bothHasMore.audit ? bothNextCursors.audit : null,
        errors: bothHasMore.errors ? bothNextCursors.errors : null,
      };
      const stack = [...bothCursorStack, pair];
      setBothCursorStack(stack);
      fetchPage(pair);
      return;
    }
    if (!hasMore || !nextCursor) return;
    const stack = [...cursorStack, nextCursor];
    setCursorStack(stack);
    fetchPage(nextCursor);
  };

  const prevPage = () => {
    if (mode === 'both') {
      if (bothCursorStack.length <= 1) return;
      const stack = bothCursorStack.slice(0, -1);
      setBothCursorStack(stack);
      fetchPage(stack[stack.length - 1]);
      return;
    }
    if (cursorStack.length <= 1) return;
    const stack = cursorStack.slice(0, -1);
    setCursorStack(stack);
    fetchPage(stack[stack.length - 1]);
  };

  const refresh = () => {
    setCursorStack([null]);
    setBothCursorStack([{ audit: null, errors: null }]);
    if (mode === 'both') {
      fetchPage({ audit: null, errors: null });
    } else {
      fetchPage(null);
    }
  };

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await auditLogsAPI.verifyIntegrity();
      setVerifyResult(res?.data || { ok: false, scanned: 0 });
    } catch (err) {
      setVerifyResult({ ok: false, scanned: 0, brokenReason: err?.message || 'Verify failed' });
    } finally {
      setVerifying(false);
    }
  };

  // CSV export — auth-aware (fetch with Bearer + blob URL). A plain <a href>
  // can't carry the Authorization header, so we read the streamed CSV into a
  // Blob and click a synthesized object-URL. Errors surface as a small alert
  // so the admin gets feedback if the streamed export rejects mid-flight.
  //
  // 'both' mode triggers two downloads in sequence — one per collection — so
  // the admin keeps the same filename + provenance per file. Merging into one
  // CSV would lose schema (audit and error rows have different columns).
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      if (mode === 'errors') {
        await errorLogsAPI.downloadCsv({
          q: filters.q, source: filters.errorSource, level: filters.errorLevel,
          fingerprint: filters.fingerprint, requestId: filters.requestId, actorId: filters.actorId,
          dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        });
      } else if (mode === 'audit') {
        await auditLogsAPI.downloadCsv({
          actionCategory: filters.actionCategory, success: filters.success, q: filters.q,
          actorId: filters.actorId, targetId: filters.targetId, requestId: filters.requestId,
          dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        });
      } else {
        // Both — sequential so the browser doesn't block the second download.
        await auditLogsAPI.downloadCsv({
          actionCategory: filters.actionCategory, success: filters.success, q: filters.q,
          actorId: filters.actorId, targetId: filters.targetId, requestId: filters.requestId,
          dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        });
        await errorLogsAPI.downloadCsv({
          q: filters.q, source: filters.errorSource, level: filters.errorLevel,
          fingerprint: filters.fingerprint, requestId: filters.requestId, actorId: filters.actorId,
          dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        });
      }
    } catch (err) {
      console.error('CSV export failed:', err);
      // eslint-disable-next-line no-alert
      alert(err?.message || 'CSV export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Clear a single filter (used by the active-filter banner's chip × buttons).
  const clearFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: '' }));
  };

  const currentPageLabel = useMemo(
    () => (mode === 'both' ? bothCursorStack.length : cursorStack.length),
    [cursorStack.length, bothCursorStack.length, mode]
  );

  const isErrors = mode === 'errors';
  const isAudit = mode === 'audit';
  const isBoth = mode === 'both';
  // Grouping is supported in single-mode views (Audit or Errors), not in
  // "Both" — the two aggregation shapes don't merge cleanly.
  const canGroup = isAudit || isErrors;
  const isGrouped = canGroup && groupBy;

  // ----- Render ----------------------------------------------------------
  // Page title is owned by AdminDashboardPage's top header ("Audit & Error
  // Logs / Tamper-evident record…") — we don't duplicate it inside the tab.
  // The action-button row sits flush to the right above the segmented control.
  // Action buttons (Verify Integrity / Export CSV / Refresh) live up in
  // AdminDashboardPage's top header via a portal, so they sit next to the
  // "Audit & Error Logs" page title — matching every other admin tab's
  // Refresh placement. We only render them inline if no portal slot was
  // provided (defensive fallback; in practice AdminDashboardPage always
  // supplies one).
  const headerActions = (
    <>
      {/* Verify integrity only meaningful for audit (hash chain). */}
      {isAudit && (
        <Button
          variant="outline"
          leftIcon={<FiShield />}
          loading={verifying}
          onClick={verify}
          title="Walk the hash chain to detect tampering"
        >
          Verify Integrity
        </Button>
      )}
      {/* Export CSV — works in every mode. In "Both" it triggers one
          download per collection (audit + errors) so each file keeps its
          native schema. Hidden in grouped views since those are
          aggregations, not row data. */}
      {!isGrouped && (
        <Button
          variant="outline"
          leftIcon={<FiDownload />}
          loading={exporting}
          onClick={exportCsv}
          title={isBoth
            ? 'Downloads two CSVs — one for audit, one for errors. Each capped at 50,000 rows.'
            : 'Streamed CSV — capped at 50,000 rows'}
        >
          {isBoth ? 'Export CSVs' : 'Export CSV'}
        </Button>
      )}
      <RefreshButton onClick={refresh} loading={loading} />
    </>
  );

  return (
    <div>
      {headerActionsEl
        ? createPortal(headerActions, headerActionsEl)
        : (
          <div className="flex items-center justify-end mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">{headerActions}</div>
          </div>
        )}

      {/* Mode segmented control + Group-by toggle */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div
          role="tablist"
          className="inline-flex rounded-[9px] border overflow-hidden"
          style={{ borderColor: '#e8e2d6', background: '#fdfbf6' }}
        >
          {[
            { id: 'audit',  label: 'Audit',  icon: <FiShield className="text-[11px]" /> },
            { id: 'errors', label: 'Errors', icon: <FiAlertTriangle className="text-[11px]" /> },
            { id: 'both',   label: 'Both',   icon: <FiActivity className="text-[11px]" /> },
          ].map((opt) => {
            const active = mode === opt.id;
            return (
              <button
                key={opt.id}
                role="tab"
                aria-selected={active}
                onClick={() => { setMode(opt.id); setCursorStack([null]); }}
                className={`px-3 py-1.5 text-[11.5px] font-semibold flex items-center gap-1.5 transition-colors ${active ? 'text-white' : 'text-[#7a7060] hover:bg-[#f8f5ef]'}`}
                style={active ? { background: '#145f5f' } : undefined}
              >
                {opt.icon}
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Group-by toggle — available in single-mode views (Audit or Errors). */}
        {canGroup && (
          <div className="flex items-center gap-1.5 text-[11.5px] text-[#08111f]">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={groupBy}
                onChange={(e) => setGroupBy(e.target.checked)}
                className="accent-[#145f5f]"
              />
              <span className="font-semibold">
                {isAudit ? 'Roll up by action' : 'Group by error signature'}
              </span>
            </label>
            <InfoPopover label="What does this do?" width={380}>
              <div className="space-y-2.5">
                <div>
                  <strong>Rolled-up view</strong> — collapses similar events into one
                  row with a count.{' '}
                  {isAudit
                    ? <>For audit, this means one row per action — e.g. "<code>user.login</code> happened 312 times today, by 47 different people."</>
                    : <>For errors, this means one row per unique stack trace — e.g. "<code>TypeError</code> at <code>companyController.js:188</code> hit 47 users this week."</>}
                  {' '}Best for your morning sweep: what's been happening, what's broken, what's getting worse?
                </div>
                <div>
                  <strong>Every-event view</strong> — every single entry as its own
                  row, with the full who/when/where/which-request. Best for chasing
                  down a specific incident or matching a customer report.
                </div>
                <div className="text-[#7a7060] italic">
                  Rolled-up view looks at the last 30 days by default. Set a wider
                  date range to roll up older history too.
                </div>
              </div>
            </InfoPopover>
          </div>
        )}
      </div>

      {/* Verify result banner. When broken, the brokenAt row id is clickable
          (jumps the search box straight to that row — supported by the
          backend smart-search adding `_id` to the OR clause for 24-hex
          inputs) and ships with a Copy chip so admins can grab it for a
          backfill script invocation. */}
      {verifyResult && (
        <div
          className="mb-3 px-3 py-2 rounded-[9px] border flex items-center gap-2 text-[12px] flex-wrap"
          style={verifyResult.ok
            ? { background: '#e8efe5', borderColor: '#d3e2cb', color: '#4a6b3f' }
            : { background: '#f2e9e6', borderColor: '#e6d2cb', color: '#9a564a' }}
        >
          {verifyResult.ok ? <FiCheckCircle /> : <FiAlertCircle />}
          {verifyResult.ok ? (
            <span>
              <strong>Chain intact.</strong> Verified {verifyResult.scanned} entries in {verifyResult.durationMs}ms.
            </span>
          ) : (
            <span className="flex items-center gap-2 flex-wrap">
              <span>
                <strong>Chain broken.</strong>{' '}
                {verifyResult.brokenReason || 'Verification failed'}
              </span>
              {verifyResult.brokenAt && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="opacity-80">at</span>
                  <button
                    type="button"
                    onClick={() => handleFilterChange('q', verifyResult.brokenAt)}
                    title="Click to filter the table to this row"
                    className="px-1.5 py-[1px] rounded-[5px] text-[10.5px] font-semibold hover:bg-[#e6d2cb] hover:underline"
                    style={{ fontFamily: "'JetBrains Mono', monospace", background: 'rgba(154,86,74,0.10)' }}
                  >
                    {verifyResult.brokenAt}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(verifyResult.brokenAt); } catch (_) {}
                    }}
                    title="Copy row id to clipboard"
                    className="px-1.5 py-[1px] rounded-[5px] text-[9.5px] font-semibold bg-white/70 hover:bg-white"
                  >
                    Copy
                  </button>
                </span>
              )}
            </span>
          )}
          <button onClick={() => setVerifyResult(null)} className="ml-auto text-[11px] underline opacity-70 hover:opacity-100">Dismiss</button>
        </div>
      )}

      {/* Filters — labeled grid. Each control sits in its own labeled column
          so single-mode views don't stretch to 100% and the date pickers
          aren't unlabeled "mm/dd/yyyy" mystery boxes. Search spans two cols
          on larger screens; everything else is one col. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <FilterField label="Search" className="lg:col-span-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7060] text-[13px]" />
            <input
              type="text"
              placeholder={isErrors ? 'Error message, path, actor…' : 'Actor, target, IP, details…'}
              value={filters.q}
              onChange={(e) => handleFilterChange('q', e.target.value)}
              className="w-full pl-9 pr-3 border-[1.5px] rounded-[9px] py-[9px] text-[12.5px] outline-none transition-shadow focus:shadow-sm focus:border-[#145f5f40]"
              style={{ borderColor: '#ddd6c6' }}
            />
          </div>
        </FilterField>

        <FilterField label="From">
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            className="w-full border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
            style={{ borderColor: '#ddd6c6' }}
          />
        </FilterField>

        <FilterField label="To">
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            className="w-full border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
            style={{ borderColor: '#ddd6c6' }}
          />
        </FilterField>

        {/* Audit-side filters — in "Both" mode the label is prefixed with
            "Audit ·" so admins can tell which stream the filter applies to. */}
        {(isAudit || isBoth) && (
          <>
            <FilterField label="Category" scopeLabel={isBoth ? 'Audit' : null} scopeTone="audit">
              <select
                value={filters.actionCategory}
                onChange={(e) => handleFilterChange('actionCategory', e.target.value)}
                className="w-full border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
                style={{ borderColor: '#ddd6c6' }}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Result" scopeLabel={isBoth ? 'Audit' : null} scopeTone="audit">
              <select
                value={filters.success}
                onChange={(e) => handleFilterChange('success', e.target.value)}
                className="w-full border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
                style={{ borderColor: '#ddd6c6' }}
              >
                <option value="">All results</option>
                <option value="true">Success only</option>
                <option value="false">Failures only</option>
              </select>
            </FilterField>
          </>
        )}

        {/* Error-side filters — same scoping treatment in "Both" mode. */}
        {(isErrors || isBoth) && (
          <>
            <FilterField label="Source" scopeLabel={isBoth ? 'Errors' : null} scopeTone="errors">
              <select
                value={filters.errorSource}
                onChange={(e) => handleFilterChange('errorSource', e.target.value)}
                className="w-full border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
                style={{ borderColor: '#ddd6c6' }}
              >
                {ERROR_SOURCE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Severity" scopeLabel={isBoth ? 'Errors' : null} scopeTone="errors">
              <select
                value={filters.errorLevel}
                onChange={(e) => handleFilterChange('errorLevel', e.target.value)}
                className="w-full border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
                style={{ borderColor: '#ddd6c6' }}
              >
                {ERROR_LEVEL_OPTIONS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </FilterField>
          </>
        )}
      </div>

      {/* Active-filter banner — shows when a deep-link target/actor/requestId/
          fingerprint is filtering the view. Helps the admin understand why
          they're seeing a narrowed set, with one-click clears. */}
      {(filters.targetId || filters.actorId || filters.requestId || filters.fingerprint) && (
        <div
          className="mb-3 px-3 py-2 rounded-[9px] border flex items-center gap-2 text-[11.5px] flex-wrap"
          style={{ background: '#fdfbf6', borderColor: '#e8e2d6', color: '#5a4e3a' }}
        >
          <span className="font-semibold text-[#08111f]">Filtering by:</span>
          {filters.targetId && (
            <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full bg-white border" style={{ borderColor: '#e8e2d6' }}>
              <span className="text-[#7a7060]">target</span>
              <code className="text-[10.5px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{filters.targetId.slice(0, 10)}…</code>
              <button onClick={() => clearFilter('targetId')} className="text-[#9a9080] hover:text-[#08111f]" aria-label="Clear target filter"><FiX className="text-[10px]" /></button>
            </span>
          )}
          {filters.actorId && (
            <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full bg-white border" style={{ borderColor: '#e8e2d6' }}>
              <span className="text-[#7a7060]">actor</span>
              <code className="text-[10.5px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{filters.actorId.slice(0, 10)}…</code>
              <button onClick={() => clearFilter('actorId')} className="text-[#9a9080] hover:text-[#08111f]" aria-label="Clear actor filter"><FiX className="text-[10px]" /></button>
            </span>
          )}
          {filters.requestId && (
            <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full bg-white border" style={{ borderColor: '#e8e2d6' }}>
              <span className="text-[#7a7060]">request</span>
              <code className="text-[10.5px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{filters.requestId}</code>
              <button onClick={() => clearFilter('requestId')} className="text-[#9a9080] hover:text-[#08111f]" aria-label="Clear request filter"><FiX className="text-[10px]" /></button>
            </span>
          )}
          {filters.fingerprint && (
            <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full bg-white border" style={{ borderColor: '#e8e2d6' }}>
              <span className="text-[#7a7060]">fingerprint</span>
              <code className="text-[10.5px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{filters.fingerprint.slice(0, 12)}…</code>
              <button onClick={() => clearFilter('fingerprint')} className="text-[#9a9080] hover:text-[#08111f]" aria-label="Clear fingerprint filter"><FiX className="text-[10px]" /></button>
            </span>
          )}
        </div>
      )}

      {/* Table — inner overflow-x-auto matches every other admin table so the
          rightmost column (SUMMARY) doesn't get clipped on narrower viewports. */}
      <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#e8e2d6' }}>
        <div className="overflow-x-auto cs-table-scroll">
        <table className="w-full text-left min-w-[960px]">
          {/* Headers adapt to mode + grouping */}
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[#7a7060] border-b" style={{ borderColor: '#e8e2d6', background: '#fdfbf6' }}>
              {isGrouped && isAudit ? (
                <>
                  <th className="px-4 py-2.5 font-semibold">Last seen</th>
                  <th className="px-4 py-2.5 font-semibold">Action</th>
                  <th className="px-4 py-2.5 font-semibold">Category</th>
                  <th className="px-4 py-2.5 font-semibold">Count</th>
                  <th className="px-4 py-2.5 font-semibold">Distinct actors</th>
                  <th className="px-4 py-2.5 font-semibold">Distinct targets</th>
                </>
              ) : isGrouped ? (
                <>
                  <th className="px-4 py-2.5 font-semibold">Last seen</th>
                  <th className="px-4 py-2.5 font-semibold">Error</th>
                  <th className="px-4 py-2.5 font-semibold">Level</th>
                  <th className="px-4 py-2.5 font-semibold">Count</th>
                  <th className="px-4 py-2.5 font-semibold">Affected users</th>
                  <th className="px-4 py-2.5 font-semibold">Routes</th>
                  <th className="px-4 py-2.5 font-semibold w-[44px]" aria-label="Actions"></th>
                </>
              ) : isErrors ? (
                <>
                  <th className="px-4 py-2.5 font-semibold">When</th>
                  <th className="px-4 py-2.5 font-semibold">Source</th>
                  <th className="px-4 py-2.5 font-semibold">Level</th>
                  <th className="px-4 py-2.5 font-semibold">Error</th>
                  <th className="px-4 py-2.5 font-semibold">Actor</th>
                  <th className="px-4 py-2.5 font-semibold">Path</th>
                  <th className="px-4 py-2.5 font-semibold w-[44px]" aria-label="Actions"></th>
                </>
              ) : (
                <>
                  <th className="px-4 py-2.5 font-semibold">When</th>
                  <th className="px-4 py-2.5 font-semibold">Actor</th>
                  <th className="px-4 py-2.5 font-semibold">{isBoth ? 'Action / Error' : 'Action'}</th>
                  <th className="px-4 py-2.5 font-semibold">Target</th>
                  <th className="px-4 py-2.5 font-semibold">IP</th>
                  <th className="px-4 py-2.5 font-semibold">Summary</th>
                  <th className="px-4 py-2.5 font-semibold w-[44px]" aria-label="Actions"></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && groups.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-[12px] text-[#7a7060]">
                  <span className="animate-pulse">Loading…</span>
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && groups.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-[12px] text-[#7a7060]">
                  {isErrors
                    ? 'No errors match your filters.'
                    : isBoth
                      ? 'No entries match your filters.'
                      : 'No audit entries match your filters.'}
                </td>
              </tr>
            )}

            {/* Grouped audit rows — one row per action with counts */}
            {isGrouped && isAudit && groups.map((g) => {
              const palette = CATEGORY_PALETTE[g.actionCategory] || CATEGORY_PALETTE.system;
              return (
                <tr
                  key={g.action}
                  className="border-b last:border-0 transition-colors hover:bg-[#f8f5ef] cursor-pointer"
                  style={{ borderColor: '#ddd6c6' }}
                  onClick={() => setSelectedRow({ ...g.latestRow, __groupCount: g.count, __groupedView: true })}
                >
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f] whitespace-nowrap">{fmtDate(g.lastSeenAt)}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold w-fit" style={{ background: palette.bg, color: palette.color }}>
                      {g.action}
                    </span>
                    {g.failureCount > 0 && (
                      <div className="text-[10px] mt-0.5 font-semibold" style={{ color: '#9a564a' }}>
                        {g.failureCount} failure{g.failureCount === 1 ? '' : 's'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-[#7a7060]">{g.actionCategory}</td>
                  <td className="px-4 py-2.5 text-[12px] font-bold text-[#08111f]">{g.count}</td>
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f]">{g.distinctActorsCount ?? 0}</td>
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f]">{g.distinctTargetsCount ?? 0}</td>
                  <td className="px-4 py-2.5 text-right">
                    <ViewButton
                      onClick={(e) => { e.stopPropagation(); setSelectedRow({ ...g.latestRow, __groupCount: g.count, __groupedView: true }); }}
                      title="View details"
                    />
                  </td>
                </tr>
              );
            })}

            {/* Grouped error rows — one row per fingerprint with counts */}
            {isGrouped && isErrors && groups.map((g) => {
              const palette = ERROR_LEVEL_PALETTE[g.latestLevel] || ERROR_LEVEL_PALETTE.error;
              return (
                <tr
                  key={g.fingerprint}
                  className="border-b last:border-0 transition-colors hover:bg-[#f8f5ef] cursor-pointer"
                  style={{ borderColor: '#ddd6c6' }}
                  onClick={() => setSelectedRow({ ...g.latestRow, __groupCount: g.count, __groupedView: true })}
                >
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f] whitespace-nowrap">{fmtDate(g.lastSeenAt)}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-[12px] font-semibold text-[#08111f] truncate max-w-[320px]">{g.latestErrorName}: {g.latestErrorMessage}</div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleFilterChange('fingerprint', g.fingerprint); }}
                      title="Click to filter by this fingerprint"
                      className="text-[10px] text-[#7a7060] hover:text-[#145f5f] hover:underline"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      fp {shortFingerprint(g.fingerprint)}…
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase" style={{ background: palette.bg, color: palette.color }}>
                      {g.latestLevel}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[12px] font-bold text-[#08111f]">{g.count}</td>
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f]">{g.affectedUsersCount ?? 0}</td>
                  <td className="px-4 py-2.5 text-[10.5px] text-[#7a7060] truncate max-w-[200px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {(g.affectedRoutes || []).filter(Boolean).slice(0, 3).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <ViewButton
                      onClick={(e) => { e.stopPropagation(); setSelectedRow({ ...g.latestRow, __groupCount: g.count, __groupedView: true }); }}
                      title="View error group"
                    />
                  </td>
                </tr>
              );
            })}

            {/* Ungrouped error rows */}
            {!isGrouped && isErrors && rows.map((row) => {
              const palette = ERROR_LEVEL_PALETTE[row.level] || ERROR_LEVEL_PALETTE.error;
              return (
                <tr
                  key={row._id}
                  className="border-b last:border-0 transition-colors hover:bg-[#f8f5ef] cursor-pointer"
                  style={{ borderColor: '#ddd6c6' }}
                  onClick={() => setSelectedRow(row)}
                >
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f] whitespace-nowrap">{fmtDate(row.createdAt)}</td>
                  <td className="px-4 py-2.5 text-[11px] uppercase font-semibold text-[#7a7060]">{row.source}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase" style={{ background: palette.bg, color: palette.color }}>
                      {row.level}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-[12px] font-semibold text-[#08111f] truncate max-w-[260px]">{row.errorName}: {row.errorMessage}</div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleFilterChange('fingerprint', row.fingerprint); }}
                      title="Click to filter by this fingerprint"
                      className="text-[10px] text-[#7a7060] hover:text-[#145f5f] hover:underline"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      fp {shortFingerprint(row.fingerprint)}…
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f] max-w-[140px] truncate">{row.actorName || <span className="italic text-[#9a9080]">—</span>}</td>
                  <td className="px-4 py-2.5 text-[10.5px] text-[#7a7060] truncate max-w-[180px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.path || ''}</td>
                  <td className="px-4 py-2.5 text-right">
                    <ViewButton
                      onClick={(e) => { e.stopPropagation(); setSelectedRow(row); }}
                      title="View error"
                    />
                  </td>
                </tr>
              );
            })}

            {/* Audit (and 'both') rows */}
            {!isGrouped && !isErrors && rows.map((row) => {
              const isErrorRow = row.__kind === 'error';
              const palette = isErrorRow
                ? (ERROR_LEVEL_PALETTE[row.level] || ERROR_LEVEL_PALETTE.error)
                : (CATEGORY_PALETTE[row.actionCategory] || CATEGORY_PALETTE.system);
              const success = !isErrorRow && row.success !== false;
              return (
                <tr
                  key={row._id}
                  className="border-b last:border-0 transition-colors hover:bg-[#f8f5ef] cursor-pointer"
                  style={{ borderColor: '#ddd6c6' }}
                  onClick={() => setSelectedRow(row)}
                >
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f] whitespace-nowrap">{fmtDate(row.createdAt)}</td>
                  <td className="px-4 py-2.5 text-[11.5px] text-[#08111f]">
                    <div className="flex items-center gap-2 max-w-[200px]">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                        style={{ background: 'linear-gradient(135deg, #1a7373 0%, #145f5f 60%, #0d4848 100%)' }}>
                        {(row.actorName || '?').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || <FiUser />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">
                          {row.actorName
                            ? row.actorName
                            : <span className="italic text-[#9a9080]">{row.actor ? 'Deleted user' : 'Anonymous'}</span>
                          }
                        </div>
                        <div className="text-[9.5px] text-[#7a7060] truncate">
                          {row.actorRole || (!row.actor && !row.actorName ? 'unauthenticated' : '')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {isErrorRow ? (
                      <div>
                        <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase" style={{ background: palette.bg, color: palette.color }}>
                          {row.level} · {row.source}
                        </span>
                        <div className="text-[11px] text-[#08111f] font-semibold truncate max-w-[220px] mt-0.5">{row.errorName}: {row.errorMessage}</div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold w-fit" style={{ background: palette.bg, color: palette.color }}>{row.action}</span>
                        {!success && (
                          <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold" style={{ color: '#9a564a' }}>
                            <FiAlertCircle className="text-[10px]" /> failure
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-[#08111f] max-w-[160px] truncate">{row.targetName || (isErrorRow ? row.path : null) || <span className="italic text-[#9a9080]">—</span>}</td>
                  <td className="px-4 py-2.5 text-[10.5px] text-[#7a7060]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.ipAddress || ''}</td>
                  <td className="px-4 py-2.5 text-[11px] text-[#7a7060] max-w-[260px] truncate">
                    {isErrorRow ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleFilterChange('fingerprint', row.fingerprint); }}
                        title="Click to filter by this fingerprint"
                        className="hover:text-[#145f5f] hover:underline"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        fp {shortFingerprint(row.fingerprint)}…
                      </button>
                    ) : summarizeAudit(row)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <ViewButton
                      onClick={(e) => { e.stopPropagation(); setSelectedRow(row); }}
                      title="View details"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Pagination — every non-grouped mode (incl. Both) gets cursor paging.
            Single-mode tracks one cursor; Both-mode tracks a pair of cursors
            (one per underlying stream) so each side can be advanced
            independently and the merged page can be reconstructed at depth. */}
        {!isGrouped && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t" style={{ borderColor: '#e8e2d6' }}>
            <div className="text-[11px] text-[#7a7060]">
              Page {currentPageLabel} · {rows.length} {isBoth ? 'merged entries' : 'entries'} shown
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={prevPage}
                disabled={loading || (isBoth ? bothCursorStack.length <= 1 : cursorStack.length <= 1)}
                className="px-3 py-1.5 border rounded-[8px] text-[11.5px] font-semibold disabled:opacity-40 hover:bg-[#f8f5ef]"
                style={{ borderColor: '#e8e2d6', color: '#7a7060' }}
              >
                ← Previous
              </button>
              <button
                onClick={nextPage}
                disabled={loading || (isBoth ? (!bothHasMore.audit && !bothHasMore.errors) : !hasMore)}
                className="px-3 py-1.5 border rounded-[8px] text-[11.5px] font-semibold disabled:opacity-40 hover:bg-[#f8f5ef]"
                style={{ borderColor: '#e8e2d6', color: '#7a7060' }}
              >
                Next page →
              </button>
            </div>
          </div>
        )}
        {isGrouped && (
          <div className="px-4 py-2.5 border-t text-[11px] text-[#7a7060]" style={{ borderColor: '#e8e2d6' }}>
            Showing {groups.length} unique {isAudit ? 'action' : 'error signature'}{groups.length === 1 ? '' : 's'} from the last 30 days. Set a wider date range to see older.
          </div>
        )}
      </div>

      {/*
        Phase 2 plans (intentionally NOT rendered on screen anymore — this
        belongs in the roadmap doc, not in the admin's working view):
          - Audit:  external append-only S3 Object Lock mirror, daily
                    summary email, anomaly detection rules.
          - Errors: Sentry / DataDog mirror, source-map upload pipeline
                    for legible frontend stacks, email alerts on critical
                    errors and new fingerprints.
        See docs/audit-log-roadmap.md.
      */}

      {/* Detail modal */}
      {selectedRow && (
        <AuditLogDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}
